import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Row,
  Col,
  InputGroup,
  Form,
  Pagination,
  Badge,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  FaPlus,
  FaEdit,
  FaSearch,
  FaSortUp,
  FaSortDown,
  FaSort,
  FaToggleOn,
  FaToggleOff,
  FaTrophy,
} from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import AuthStorage from '~/Utils/AuthStorage';
import { caracteristicas } from '~/constant';
import {
  listarImobiliariasCadastro,
  alterarStatusImobiliaria,
  alterarRankingImobiliaria,
} from '~/Services/Service';
import ModalImobiliaria from './components/ModalImobiliaria';

const PAGE_SIZE = 20;
const thSortStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

const CadastroImobiliarias = () => {
  const [imobiliarias, setImobiliarias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroNome, setFiltroNome] = useState('');
  const [pagina, setPagina] = useState(1);
  const [ordenacao, setOrdenacao] = useState({ campo: 'nome', direcao: 'asc' });

  const [showModal, setShowModal] = useState(false);
  const [imobSelecionada, setImobSelecionada] = useState(null);

  const [confirmacao, setConfirmacao] = useState({
    show: false,
    titulo: '',
    mensagem: '',
    onConfirm: null,
  });

  const temPermissao = AuthStorage.temCaracteristicas([
    caracteristicas.CadastraImobiliaria,
  ]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await listarImobiliariasCadastro();
      setImobiliarias(Array.isArray(resp) ? resp : []);
    } catch (err) {
      const mensagem =
        err && err.message ? err.message : 'Erro ao carregar imobiliárias.';
      toast.error(mensagem);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleOrdenacao = (campo) => {
    setOrdenacao((prev) => {
      if (prev.campo === campo) {
        return { campo, direcao: prev.direcao === 'asc' ? 'desc' : 'asc' };
      }
      return { campo, direcao: 'asc' };
    });
    setPagina(1);
  };

  const iconeOrdenacao = (campo) => {
    if (ordenacao.campo !== campo) {
      return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    }
    return ordenacao.direcao === 'asc' ? (
      <FaSortUp style={{ marginLeft: 4 }} />
    ) : (
      <FaSortDown style={{ marginLeft: 4 }} />
    );
  };

  const filtradas = useMemo(() => {
    const termo = filtroNome.trim().toLowerCase();
    let lista = imobiliarias;
    if (termo) {
      lista = lista.filter(
        (i) => i.nome && i.nome.toLowerCase().includes(termo)
      );
    }
    const dir = ordenacao.direcao === 'asc' ? 1 : -1;
    const campo = ordenacao.campo;
    return [...lista].sort((a, b) => {
      const va = a[campo];
      const vb = b[campo];
      if (typeof va === 'boolean' && typeof vb === 'boolean') {
        return va === vb ? 0 : va ? -dir : dir;
      }
      const sa = (va == null ? '' : String(va)).toLowerCase();
      const sb = (vb == null ? '' : String(vb)).toLowerCase();
      if (sa < sb) return -1 * dir;
      if (sa > sb) return 1 * dir;
      return 0;
    });
  }, [imobiliarias, filtroNome, ordenacao]);

  const total = filtradas.length;
  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const inicio = (paginaAtual - 1) * PAGE_SIZE;
  const visiveis = filtradas.slice(inicio, inicio + PAGE_SIZE);

  useEffect(() => {
    setPagina(1);
  }, [filtroNome]);

  const handleNovo = () => {
    setImobSelecionada(null);
    setShowModal(true);
  };

  const handleEditar = (item) => {
    setImobSelecionada(item);
    setShowModal(true);
  };

  const handleSalvar = () => {
    setShowModal(false);
    carregar();
  };

  const fecharConfirmacao = () => {
    setConfirmacao({ show: false, titulo: '', mensagem: '', onConfirm: null });
  };

  const handleToggleAtivo = (item) => {
    const novoAtivo = !item.ativo;
    setConfirmacao({
      show: true,
      titulo: novoAtivo ? 'Ativar imobiliária' : 'Inativar imobiliária',
      mensagem: `Tem certeza que deseja ${
        novoAtivo ? 'ativar' : 'inativar'
      } a imobiliária "${item.nome}"?`,
      onConfirm: async (confirmado) => {
        fecharConfirmacao();
        if (!confirmado) return;
        try {
          await alterarStatusImobiliaria(item.codigo, novoAtivo);
          toast.success(
            novoAtivo
              ? 'Imobiliária ativada com sucesso.'
              : 'Imobiliária inativada com sucesso.'
          );
          carregar();
        } catch (err) {
          const mensagem =
            err && err.message ? err.message : 'Erro ao alterar status.';
          toast.error(mensagem);
        }
      },
    });
  };

  const handleToggleRanking = (item) => {
    const novoRanking = !item.ranking_venda;
    setConfirmacao({
      show: true,
      titulo: novoRanking
        ? 'Liberar para Ranking'
        : 'Remover do Ranking',
      mensagem: novoRanking
        ? `A imobiliária "${item.nome}" passará a aparecer no Ranking de Vendas e no Relatório Leads x Vendas. Confirma?`
        : `A imobiliária "${item.nome}" e seus corretores deixarão de aparecer no Ranking de Vendas e no Relatório Leads x Vendas. Confirma?`,
      onConfirm: async (confirmado) => {
        fecharConfirmacao();
        if (!confirmado) return;
        try {
          await alterarRankingImobiliaria(item.codigo, novoRanking);
          toast.success('Configuração de ranking atualizada.');
          carregar();
        } catch (err) {
          const mensagem =
            err && err.message ? err.message : 'Erro ao alterar ranking.';
          toast.error(mensagem);
        }
      },
    });
  };

  const handlePagina = (pg) => {
    if (pg < 1 || pg > totalPaginas) return;
    setPagina(pg);
  };

  const renderPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const pages = [];
    const maxBtn = 5;
    let start = Math.max(1, paginaAtual - Math.floor(maxBtn / 2));
    const end = Math.min(totalPaginas, start + maxBtn - 1);
    if (end - start < maxBtn - 1) start = Math.max(1, end - maxBtn + 1);
    pages.push(
      <Pagination.First
        key="first"
        disabled={paginaAtual === 1}
        onClick={() => {
          handlePagina(1);
        }}
      />,
      <Pagination.Prev
        key="prev"
        disabled={paginaAtual === 1}
        onClick={() => {
          handlePagina(paginaAtual - 1);
        }}
      />
    );
    if (start > 1) pages.push(<Pagination.Ellipsis key="ell-start" disabled />);
    for (let i = start; i <= end; i += 1) {
      const pg = i;
      pages.push(
        <Pagination.Item
          key={pg}
          active={pg === paginaAtual}
          onClick={() => {
            handlePagina(pg);
          }}
        >
          {pg}
        </Pagination.Item>
      );
    }
    if (end < totalPaginas) pages.push(<Pagination.Ellipsis key="ell-end" disabled />);
    pages.push(
      <Pagination.Next
        key="next"
        disabled={paginaAtual === totalPaginas}
        onClick={() => {
          handlePagina(paginaAtual + 1);
        }}
      />,
      <Pagination.Last
        key="last"
        disabled={paginaAtual === totalPaginas}
        onClick={() => {
          handlePagina(totalPaginas);
        }}
      />
    );
    return (
      <div className="d-flex justify-content-between align-items-center px-3 py-2 flex-wrap">
        <small className="text-muted">
          Mostrando {Math.min(inicio + 1, total)} até{' '}
          {Math.min(inicio + PAGE_SIZE, total)} de {total} registros
        </small>
        <Pagination size="sm" className="mb-0">
          {pages}
        </Pagination>
      </div>
    );
  };

  if (loading && imobiliarias.length === 0) return <Loading />;

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Cadastro de Imobiliárias" />

      <Card className="shadow-sm mb-3">
        <Card.Body className="py-2">
          <Row className="align-items-end">
            <Col md={6} className="mb-2 mb-md-0">
              <Form.Label as="small" className="mb-1 font-weight-bold">
                Busca por nome
              </Form.Label>
              <InputGroup size="sm">
                <InputGroup.Prepend>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  placeholder="Digite parte do nome..."
                  value={filtroNome}
                  onChange={(e) => {
                    setFiltroNome(e.target.value);
                  }}
                />
              </InputGroup>
            </Col>
            <Col md={6} className="text-right">
              <Button
                size="sm"
                variant="primary"
                onClick={handleNovo}
                disabled={!temPermissao}
                title={!temPermissao ? 'Sem permissão' : ''}
              >
                <FaPlus className="mr-1" /> Nova Imobiliária
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header
          className="d-flex justify-content-between align-items-center py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>
            Imobiliárias
            <span className="ml-2" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
              ({total} {total === 1 ? 'registro' : 'registros'})
            </span>
          </strong>
          {loading && <small style={{ opacity: 0.8 }}>Carregando...</small>}
        </Card.Header>
        <Card.Body className="p-0">
          {visiveis.length === 0 && !loading ? (
            <p className="text-muted text-center my-4">
              Nenhuma imobiliária encontrada.
            </p>
          ) : (
            <>
              <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th
                        style={thSortStyle}
                        onClick={() => {
                          handleOrdenacao('nome');
                        }}
                      >
                        Nome{iconeOrdenacao('nome')}
                      </th>
                      <th style={{ width: '120px' }}>Cor</th>
                      <th
                        style={thSortStyle}
                        onClick={() => {
                          handleOrdenacao('nome_gerente');
                        }}
                      >
                        Gerente{iconeOrdenacao('nome_gerente')}
                      </th>
                      <th style={{ width: '90px' }}>Logo</th>
                      <th
                        style={Object.assign({ width: '90px' }, thSortStyle)}
                        onClick={() => {
                          handleOrdenacao('ativo');
                        }}
                      >
                        Ativo{iconeOrdenacao('ativo')}
                      </th>
                      <th
                        style={Object.assign({ width: '120px' }, thSortStyle)}
                        onClick={() => {
                          handleOrdenacao('ranking_venda');
                        }}
                      >
                        Ranking{iconeOrdenacao('ranking_venda')}
                      </th>
                      <th style={{ width: '160px', textAlign: 'center' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visiveis.map((item) => (
                      <tr key={`imob-${item.codigo}`}>
                        <td>{item.nome}</td>
                        <td>
                          <span
                            style={{
                              display: 'inline-block',
                              width: 14,
                              height: 14,
                              borderRadius: '50%',
                              backgroundColor: item.cor || '#ccc',
                              border: '1px solid #ccc',
                              marginRight: 6,
                              verticalAlign: 'middle',
                            }}
                          />
                          <small className="text-muted">{item.cor || '—'}</small>
                        </td>
                        <td>{item.nome_gerente || '—'}</td>
                        <td>
                          {item.url_logo ? (
                            <img
                              src={item.url_logo}
                              alt={item.nome}
                              style={{
                                maxHeight: 24,
                                maxWidth: 70,
                                objectFit: 'contain',
                              }}
                            />
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <Badge variant={item.ativo ? 'success' : 'secondary'}>
                            {item.ativo ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </td>
                        <td>
                          <Badge
                            variant={item.ranking_venda ? 'info' : 'secondary'}
                          >
                            {item.ranking_venda ? 'Aparece' : 'Oculto'}
                          </Badge>
                        </td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            title="Editar"
                            className="mr-1"
                            disabled={!temPermissao}
                            onClick={() => {
                              handleEditar(item);
                            }}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            size="sm"
                            variant={item.ativo ? 'outline-warning' : 'outline-success'}
                            title={item.ativo ? 'Inativar' : 'Ativar'}
                            className="mr-1"
                            disabled={!temPermissao}
                            onClick={() => {
                              handleToggleAtivo(item);
                            }}
                          >
                            {item.ativo ? <FaToggleOff /> : <FaToggleOn />}
                          </Button>
                          <Button
                            size="sm"
                            variant={
                              item.ranking_venda ? 'outline-secondary' : 'outline-info'
                            }
                            title={
                              item.ranking_venda
                                ? 'Remover do Ranking'
                                : 'Liberar para Ranking'
                            }
                            disabled={!temPermissao}
                            onClick={() => {
                              handleToggleRanking(item);
                            }}
                          >
                            <FaTrophy />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {renderPaginacao()}
            </>
          )}
        </Card.Body>
      </Card>

      <ModalConfirmacao
        show={confirmacao.show}
        title={confirmacao.titulo}
        message={confirmacao.mensagem}
        onClose={(confirmado) => {
          if (confirmacao.onConfirm) confirmacao.onConfirm(confirmado);
          else fecharConfirmacao();
        }}
      />

      <ModalImobiliaria
        show={showModal}
        modo={imobSelecionada ? 'edicao' : 'cadastro'}
        imobiliariaInicial={imobSelecionada}
        onHide={() => {
          setShowModal(false);
        }}
        onSalvar={handleSalvar}
      />
    </Container>
  );
};

export default CadastroImobiliarias;
